import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { Yard, Groups, EmojiEvents, Map } from '@mui/icons-material';

const Landing: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Yard sx={{ fontSize: 48 }} />,
      title: 'Start Small or Big',
      description: 'Begin with a single plant or create a whole farm. Grow at your own pace.',
    },
    {
      icon: <Map sx={{ fontSize: 48 }} />,
      title: 'Discover & Connect',
      description: 'Find gardens and farms near you. Join a community of local growers.',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 48 }} />,
      title: 'Gamified Growing',
      description: 'Earn XP, maintain streaks, compete in leagues, and unlock achievements.',
    },
    {
      icon: <Groups sx={{ fontSize: 48 }} />,
      title: 'Collaborate',
      description: 'Share spaces, coordinate tasks, and grow together with friends.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #22c55e 0%, #06b6d4 100%)',
          color: 'white',
          pt: 8,
          pb: 12,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h1" sx={{ fontSize: '4rem', fontWeight: 'bold', mb: 2 }}>
              🌱 Botala
            </Typography>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 300 }}>
              Let's grow good, together
            </Typography>
            <Typography variant="h6" sx={{ mb: 6, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              Turn gardening into an adventure! Track your plants, complete daily quests, 
              and connect with local growers in your community.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  px: 4,
                  py: 1.5,
                }}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  px: 4,
                  py: 1.5,
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" sx={{ mb: 6, fontWeight: 'bold' }}>
          Your Gardening Journey Starts Here
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
                elevation={2}
              >
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ mb: 6, fontWeight: 'bold' }}>
            Start Your Garden Today
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 4 } }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                  Flexible Start Options
                </Typography>
                <Typography variant="body1" paragraph>
                  Whether you have a single houseplant or acres of land, Botala adapts to your needs:
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <Typography component="li" variant="body1" paragraph>
                    <strong>Start with a Plant:</strong> Perfect for apartment dwellers with a windowsill herb
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    <strong>Create a Garden:</strong> Organize your plant collection in one place
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    <strong>Define a Space:</strong> Map out your backyard, balcony, or growing area
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    <strong>Establish a Farm:</strong> Go big with GPS-tracked property management
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'white',
                  p: 4,
                  borderRadius: 2,
                  boxShadow: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Ready to grow?
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/register')}
                  sx={{ mt: 2 }}
                >
                  Start Your Journey
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;